/**
 * Utility functions for vehicle make and model dropdown transformations
 */

import { POPULAR_MAKES } from "@/constants/popularMakes";

type SelectOption = { value: string; label: string };
type OptionGroup = { optgroup: string; options: SelectOption[] };
type MakeApiItem = { makeCode?: string; makeDescription?: string };
type ModelApiItem = {
  value?: string;
  label?: string;
  modelCode?: string;
  modelDescription?: string;
};

type PopularMakeModelsMap = Record<
  string,
  { name?: string; models: SelectOption[] }
>;

/**
 * The popular make/model mapping is ~31KB of static data and is only needed
 * once the user opens the Model dropdown. Lazy-load it via a dynamic import
 * (separate webpack chunk) and cache the resolved module so subsequent calls
 * don't pay the await cost.
 */
let popularMakeModelsCache: PopularMakeModelsMap | null = null;
let popularMakeModelsPromise: Promise<PopularMakeModelsMap> | null = null;

const loadPopularMakeModels = (): Promise<PopularMakeModelsMap> => {
  if (popularMakeModelsCache) {
    return Promise.resolve(popularMakeModelsCache);
  }
  if (!popularMakeModelsPromise) {
    popularMakeModelsPromise = import("@/constants/popularMakeModels").then(
      (mod) => {
        popularMakeModelsCache = mod.POPULAR_MAKE_MODELS as PopularMakeModelsMap;
        return popularMakeModelsCache;
      },
    );
  }
  return popularMakeModelsPromise;
};

/**
 * Transform vehicle makes array into dropdown options with optgroups
 * Creates optgroups for Popular Makes and All Makes
 *
 * @param makes - Array of make objects from API
 * @param usePopularMakes - Whether to separate popular makes into optgroup
 * @returns Array of SelectOption and OptionGroup objects
 */
export const makeDropDownMask = (
  makes: MakeApiItem[] = [],
  usePopularMakes: boolean = true,
): (SelectOption | OptionGroup)[] => {
  const makeDropdownOptions: (SelectOption | OptionGroup)[] = [
    { value: "", label: "Any Make" },
  ];

  if (makes.length > 0) {
    const popularMakesData: SelectOption[] = [];
    const allMakes: SelectOption[] = [];

    makes.forEach((make: MakeApiItem) => {
      const { makeCode, makeDescription } = make;
      if (makeDescription && makeDescription !== "Any Make") {
        const makeOption: SelectOption = {
          value: makeCode || "",
          label: makeDescription || "",
        };

        // Check if in Popular Makes list
        if (
          usePopularMakes &&
          makeCode &&
          Object.prototype.hasOwnProperty.call(POPULAR_MAKES, makeCode)
        ) {
          popularMakesData.push(makeOption);
        }

        allMakes.push(makeOption);
      }
    });

    if (usePopularMakes) {
      makeDropdownOptions.push({
        optgroup: "Popular Makes",
        options: popularMakesData,
      } as OptionGroup);
    }

    makeDropdownOptions.push({
      optgroup: "All Makes",
      options: allMakes,
    } as OptionGroup);
  }

  return makeDropdownOptions;
};

/**
 * Transform models array into dropdown options with optgroups
 * Creates optgroups for Popular Models and All Models
 *
 * Async because the popular-models lookup table is lazy-loaded on first call
 * (see loadPopularMakeModels above) to keep ~31KB of static data out of the
 * initial /marketplace bundle.
 *
 * @param models - Array of model objects from API
 * @param makeCode - Vehicle make code for filtering popular models
 * @returns Promise resolving to array of SelectOption and OptionGroup objects
 */
export const modelDropDownMask = async (
  models: ModelApiItem[] = [],
  makeCode: string = "",
): Promise<(SelectOption | OptionGroup)[]> => {
  const modelDropdownOptions: (SelectOption | OptionGroup)[] = [
    { value: "", label: "Any Model" },
  ];

  const popularMakeModels = makeCode ? await loadPopularMakeModels() : null;
  const popularModelsData =
    makeCode && popularMakeModels?.[makeCode]?.models
      ? popularMakeModels[makeCode].models
      : [];

  if (models.length > 0) {
    const popularModels: SelectOption[] = [];
    const allModels: SelectOption[] = [];

    models.forEach((model: ModelApiItem) => {
      const modelOption: SelectOption = {
        value: model.value || model.modelCode || "",
        label: model.label || model.modelDescription || "",
      };

      if (modelOption.label && modelOption.label !== "Any Model") {
        // Check if in Popular Models list
        if (popularModelsData) {
          if (
            popularModelsData.some(
              (popularModel: SelectOption) =>
                popularModel.value === modelOption.value,
            )
          ) {
            popularModels.push(modelOption);
          }
        }

        allModels.push(modelOption);
      }
    });

    modelDropdownOptions.push(
      {
        optgroup: "Popular Models",
        options: popularModels,
      } as OptionGroup,
      {
        optgroup: "All Models",
        options: allModels,
      } as OptionGroup,
    );
  }

  return modelDropdownOptions;
};

/**
 * Fetch vehicle models for a given make code from API
 * Used when user selects a make
 *
 * @param makeCode - The vehicle make code to fetch models for
 * @returns Promise resolving to array of model options
 */
export const fetchModelsByMake = async (
  makeCode: string,
): Promise<ModelApiItem[]> => {
  try {
    const response = await fetch(
      `/api/psx-buy/vehicle-models?makeCode=${encodeURIComponent(makeCode)}`,
    );
    const data: ModelApiItem[] = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error(`Error fetching models for make ${makeCode}:`, error);
    return [];
  }
};

/**
 * Update popular makes configuration
 * Useful for dynamic updates based on region or user preferences
 *
 * @param makes - Object with make codes as keys
 */
export const updatePopularMakes = (
  makes: Record<string, { name?: string }>,
) => {
  Object.assign(POPULAR_MAKES, makes);
};

/**
 * Update popular models configuration
 * Useful for dynamic updates based on region or preferences.
 * Async because the popular-models data is lazy-loaded on first access.
 *
 * @param models - Object with make codes as keys and model arrays as values
 */
export const updatePopularMakeModels = async (
  models: Record<string, { name?: string; models: SelectOption[] }>,
): Promise<void> => {
  const popularMakeModels = await loadPopularMakeModels();
  Object.assign(popularMakeModels, models);
};
